import { Component, input, output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { TranslocoPipe } from '@jsverse/transloco';
import { ButtonComponent, TextareaFieldComponent } from 'htx-ui-library';

export interface Comment {
  id: string;
  author: string;
  text: string;
  date: Date | string;
  avatarUrl?: string;
  type?: string;
}

@Component({
  selector: 'app-comments',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButtonModule,
    TranslocoPipe,
    ButtonComponent,
    TextareaFieldComponent
  ],
  templateUrl: './comments.component.html',
  styleUrl: './comments.component.scss'
})
export class CommentsComponent {
  comments = input<Comment[]>([]);
  placeholder = input('');
  maxLength = input(500);
  showCounter = input(true);
  allowEmpty = input(false);

  commentAdded = output<string>();

  commentControl = new FormControl('', [
    Validators.required,
    Validators.maxLength(this.maxLength())
  ]);

  get remainingChars(): number {
    return this.maxLength() - (this.commentControl.value?.length || 0);
  }

  get isNearLimit(): boolean {
    return this.remainingChars < 50;
  }

  onKeydown(event: KeyboardEvent): void {
    if (event.ctrlKey) {
      this.submitComment();
      event.preventDefault();
    }
  }

  submitComment(): void {
    if (this.commentControl.valid && this.commentControl.value?.trim()) {
      this.commentAdded.emit(this.commentControl.value.trim());
      this.commentControl.reset();
    } else if (!this.allowEmpty()) {
      this.commentControl.markAsTouched();
    }
  }

  getInitials(name: string): string {
    return name
      .split(' ')
      .map((n) => n.charAt(0))
      .slice(0, 2)
      .join('')
      .toUpperCase();
  }

  getAvatarColor(name: string): string {
    const colors = [
      '#8b9dc3',
      '#c995a8',
      '#6eb5d1',
      '#7ec4a3',
      '#d4a574',
      '#9b8db5',
      '#b8a8a8',
      '#a8b5c8'
    ];
    const index = name.charCodeAt(0) % colors.length;
    return colors[index];
  }

  formatDate(date: Date | string): string {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    const now = new Date();
    const diffMs = now.getTime() - dateObj.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Gerade eben';
    if (diffMins < 60) return `Vor ${diffMins} Min.`;
    if (diffHours < 24) return `Vor ${diffHours} Std.`;
    if (diffDays < 7) return `Vor ${diffDays} Tag${diffDays > 1 ? 'en' : ''}`;

    return dateObj.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }
}
